import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { TerminalClause } from '@atlaskit/jql-ast';
import { useIntl } from '@atlassian/jira-intl';
import type { SearchTemplate } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { useHydrationQueryLoaderQuery$data } from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';
import type { JQLBuilderUI$data } from '@atlassian/jira-jql-builder/src/ui/__generated__/JQLBuilderUI.graphql';
import type { AdvancedContent$data } from '@atlassian/jira-filter-refinement/src/ui/filter-popup/filter-popup-content/ui/advanced/__generated__/AdvancedContent.graphql';
import type { useHydratedValues$key } from './__generated__/useHydratedValues.graphql';
import { getValuesFromClause } from './utils/get-values-from-clause/index.tsx';

export const useHydratedValues = (
	fieldId: string,
	hydratedData: useHydrationQueryLoaderQuery$data | JQLBuilderUI$data | AdvancedContent$data,
	searchTemplate: SearchTemplate,
	terminalClause?: TerminalClause,
	operandDisplayValues?: string[],
) => {
	const intl = useIntl();

	/* eslint-disable @atlassian/relay/graphql-naming, @atlassian/relay/unused-fields */
	const data = useFragment<useHydratedValues$key>(
		graphql`
			fragment useHydratedValues on Query
			@argumentDefinitions(
				includeAppTypeForJqlBuilder: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-ai-agent-avatar-with-apptype-for-jql.relayprovider"
				}
			) {
				user(accountId: $accountId) @include(if: $includeUser) {
					picture
					... on AppUser {
						appType @include(if: $includeAppTypeForJqlBuilder)
					}
				}
				jira @include(if: $includeJira) {
					jqlBuilder(cloudId: $cloudId) {
						hydrateJqlQuery(query: $jql, input: $input, viewContext: $viewContext, scope: $scope)
							@skip(if: $isFilter) {
							... on JiraJqlHydratedQuery {
								jql
								fields {
									... on JiraJqlQueryHydratedField {
										jqlTerm
										field {
											displayName
											searchTemplate {
												key
											}
											shouldShowInContext
										}
										values {
											... on JiraJqlQueryHydratedValue {
												jqlTerm
												values {
													__typename
													jqlTerm
													displayName
													... on JiraJqlProjectFieldValue {
														project {
															key
															avatar {
																small
															}
														}
													}
													... on JiraJqlStatusFieldValue {
														statusCategory {
															colorName
														}
													}
													... on JiraJqlStatusCategoryFieldValue {
														statusCategory {
															colorName
														}
													}
													... on JiraJqlIssueTypeFieldValue {
														issueTypes {
															avatar {
																xsmall
															}
														}
													}
													... on JiraJqlUserFieldValue {
														user {
															picture
															... on AppUser {
																appType @include(if: $includeAppTypeForJqlBuilder)
															}
														}
													}
													... on JiraJqlPriorityFieldValue {
														priority {
															iconUrl
														}
													}
													... on JiraJqlIssueFieldValue {
														issue {
															fields {
																edges {
																	node {
																		... on JiraSingleLineTextField {
																			text
																		}
																	}
																}
															}
														}
													}
													... on JiraJqlSprintFieldValue {
														sprint {
															state
															boardName
														}
													}
												}
											}
											... on JiraJqlQueryHydratedError {
												jqlTerm
												error {
													__typename # only interested in error presence to handle error state
												}
											}
										}
									}
								}
							}
							... on QueryError {
								message
							}
						}
						hydrateJqlQueryForFilter(id: $filterAri, viewContext: $viewContext, scope: $scope)
							@include(if: $isFilter) {
							... on JiraJqlHydratedQuery {
								jql
								fields {
									... on JiraJqlQueryHydratedField {
										jqlTerm
										field {
											displayName
											searchTemplate {
												key
											}
											shouldShowInContext
										}
										values {
											... on JiraJqlQueryHydratedValue {
												jqlTerm
												values {
													__typename
													jqlTerm
													displayName
													... on JiraJqlProjectFieldValue {
														project {
															key
															avatar {
																small
															}
														}
													}
													... on JiraJqlStatusFieldValue {
														statusCategory {
															colorName
														}
													}
													... on JiraJqlStatusCategoryFieldValue {
														statusCategory {
															colorName
														}
													}
													... on JiraJqlIssueTypeFieldValue {
														issueTypes {
															avatar {
																xsmall
															}
														}
													}
													... on JiraJqlUserFieldValue {
														user {
															picture
															... on AppUser {
																appType @include(if: $includeAppTypeForJqlBuilder)
															}
														}
													}
													... on JiraJqlPriorityFieldValue {
														priority {
															iconUrl
														}
													}
													... on JiraJqlIssueFieldValue {
														issue {
															fields {
																edges {
																	node {
																		... on JiraSingleLineTextField {
																			text
																		}
																	}
																}
															}
														}
													}
													... on JiraJqlSprintFieldValue {
														sprint {
															state
															boardName
														}
													}
												}
											}
											... on JiraJqlQueryHydratedError {
												jqlTerm
												error {
													__typename # only interested in error presence to handle error state
												}
											}
										}
									}
								}
							}
							... on QueryError {
								message
							}
						}
					}
				}
			}
		`,
		hydratedData,
	);
	/* eslint-enable @atlassian/relay/graphql-naming, @atlassian/relay/unused-fields */

	return useMemo(() => {
		if (terminalClause && data) {
			const hydratedValuesFromClause = getValuesFromClause(
				terminalClause,
				data,
				fieldId,
				operandDisplayValues,
				intl,
				searchTemplate,
			);
			if (hydratedValuesFromClause !== undefined) {
				return Array.isArray(hydratedValuesFromClause)
					? hydratedValuesFromClause
					: [hydratedValuesFromClause];
			}
		}
		return [];
	}, [terminalClause, data, fieldId, intl, operandDisplayValues, searchTemplate]);
};
